import { useMemo } from 'react';
import * as React from 'react';
import { Helmet } from 'react-helmet';
import { graphql, useStaticQuery } from 'gatsby';

import { SOCIAL_SHARE } from '../../utils/social-share-images';

export interface SEOContentful {
  includeTitleSuffix: boolean;
  noIndex: boolean;
  seoMetaDescription: string;
  seoTitle: string;
  slug: string;
  socialSharePreviewImage: {
    title: string;
    url: string;
  };
}

export interface SEOProps {
  description: string;
  image?: string;
  includeTitleSuffix?: boolean;
  meta?: [];
  noIndex?: boolean;
  title: string;
  url: string;
}

export const SEO: React.ComponentType<SEOProps> = React.memo(
  ({
    description: metaDescription,
    meta = [],
    image: metaImage,
    includeTitleSuffix = true,
    title: metaTitle,
    url: metaUrl,
    noIndex = false
  }) => {
    const { site } = useStaticQuery(graphql`
      {
        site {
          siteMetadata {
            title
            description
            author
            keywords
            siteUrl
          }
        }
      }
    `);

    const title = `${metaTitle}${
      includeTitleSuffix ? ` | ${site.siteMetadata.title} ` : ''
    }`;

    const description = metaDescription || site.siteMetadata.description;

    const imagePath = useMemo(
      () => metaImage || SOCIAL_SHARE.HomePage,
      [metaImage]
    );

    const url = `${site.siteMetadata.siteUrl}${metaUrl}`;

    return (
      <Helmet
        htmlAttributes={{ lang: 'en' }}
        link={[
          {
            href: url
          }
        ]}
        meta={[
          {
            name: `viewport`,
            content: 'width=device-width, initial-scale=1, minimum-scale=1'
          },
          {
            name: `description`,
            content: description
          },
          {
            name: 'keywords',
            content: site.siteMetadata.keywords.join(',')
          },
          {
            name: `og:site_name`,
            content: 'Hex'
          },
          {
            property: `og:title`,
            content: title
          },
          {
            property: `og:url`,
            content: url
          },
          {
            property: `og:description`,
            content: description
          },
          {
            property: `og:type`,
            content: 'website'
          },
          {
            name: `twitter:creator`,
            content: site.siteMetadata.author
          },
          {
            name: `twitter:title`,
            content: title
          },
          {
            name: `twitter:description`,
            content: description
          }
        ]
          .concat(
            imagePath
              ? [
                  {
                    property: 'og:image',
                    content: imagePath
                  },
                  {
                    property: 'og:image:width',
                    content: 1200
                  },
                  {
                    property: 'og:image:height',
                    content: 630
                  },
                  {
                    name: 'twitter:card',
                    content: 'summary_large_image'
                  },
                  {
                    property: 'twitter:image',
                    content: imagePath
                  }
                ]
              : []
          )
          .concat(
            noIndex
              ? [
                  {
                    name: 'robots',
                    content: 'noindex'
                  }
                ]
              : []
          )
          .concat(meta)}
        title={title}></Helmet>
    );
  }
);
